import React, { FC, useEffect, useMemo } from 'react';
import { useTableApi, useTableUniverse } from '~Components/TableUniverse';
import styled from '@emotion/styled';
import { OrderingWrapper } from '../TableSelector';
import Ordering from '../Ordering';
import CartProvider from '~Components/CartProvider';
import { useStorage } from '~Components/Storage';
import { useTableContext } from '~Scenes/Handy/Context/TableContext';
import { useLicenses, useSelector } from '~Hooks';
import { CartItem } from '~Types';
import Onboarding from '~Scenes/Onboarding';
import { extractServiceChargePercentage, isServiceChargeItem } from '~Utils/serviceCharge';
import { useEntryFinder } from '~Components/EntryFinder';
import { DateTime } from 'luxon';
import AccessBlocked from '~Providers/LicensesProvider/Components/AccessBlocked';
import ClaimLicense from '~Providers/LicensesProvider/Components/ClaimLicense';
import { BAEMINGO_PRODUCT } from '../../../../../__queries__';

const Wrapper = styled.div`
  display: flex;
  width: 100vw;
  height: 100%;
  background: #1c232c;
  position: relative;
`;

const HandyQSR: FC = () => {
  const { setSelectedTable } = useTableContext();

  /** Private means that the events will not sync to other devices */
  const tableApi = useTableApi({
    private: true,
  });

  const findEntryByItemId = useEntryFinder();

  const venueId = useSelector((state) => state.venueId);
  const licenses = useLicenses();

  const universe = useTableUniverse();

  const QSRCheques = useMemo(
    () =>
      universe.cheques.filter(
        (cheque) => cheque.tableId?.startsWith('POS_') && cheque.paidAmount === 0,
      ),
    [universe.cheques],
  );

  const oldPosCheques = useMemo(
    () =>
      QSRCheques.filter(
        (cheque) =>
          // Check if the cheque is older than 1 day
          DateTime.fromISO(cheque.createdAt)
            .plus({ days: 1 })
            .toMillis() < DateTime.now().toMillis(),
      ),
    [QSRCheques],
  );

  useEffect(() => {
    if (oldPosCheques.length === 0) return;

    oldPosCheques.forEach((cheque) => {
      tableApi.removeCheque(cheque.id, cheque.tableId || '');
    });
  }, [oldPosCheques]);

  // We use client ID to create cheque and table id's. Makes them unique across devices/clients
  // while the identifyer is still easily accesible through code on the same device.
  const [{ clientId }] = useStorage();

  // Set selected table to a minimal POS table
  useEffect(() => {
    setSelectedTable({ id: `POS_${clientId}` });
    // Unselect the table when we leave QSR mode
    return () => setSelectedTable(null);
  }, []);

  const handleContinue = (items?: CartItem[]) => {
    if (!items) {
      return;
    }

    // Create cheque and add items to the cheque
    const chequeId = tableApi.createCheque(`POS_${clientId}`);

    // Timeout to ensure cheque is created before adding items
    setTimeout(() => {
      /** Items that is filtered from Service Charge & Messages */
      const justRegularItems: CartItem[] = [];

      items.forEach((item) => {
        if (isServiceChargeItem(item.title)) {
          const percentage = extractServiceChargePercentage(item.title);

          tableApi.addServiceCharge({
            categoryId: item.categoryId,
            chequeId: chequeId,
            entryId: item.entryId,
            itemId: item.itemId,
            percentage,
            vatRate: item.vatRate,
          });
          return;
        }

        if (item.title.includes('[m]')) {
          tableApi.addMessage(item.title.replace('[m]', '').trim(), chequeId);
          return;
        }

        justRegularItems.push(item);
      });

      const itemsToAdd = justRegularItems.map((item) => ({
        ...item,
        // Here we don't need the special solution for amount and discount, since all items are added at the same time
        entryId: findEntryByItemId(item.itemId).id,
      }));

      tableApi.addItems(itemsToAdd, chequeId);
    }, 1);
  };

  if (licenses.isPOSAccessBlocked) {
    return <AccessBlocked isHandy />;
  }

  if (!licenses.hasHandheldPro && licenses.isHandheldProClaimable) {
    return <ClaimLicense isHandy product={BAEMINGO_PRODUCT.HANDHELD_PRO} />;
  }

  if (!venueId) {
    return null;
  }

  return (
    <Wrapper>
      <Onboarding isHandy />
      <OrderingWrapper>
        <CartProvider useSeparateTakeAwayMenu>
          <Ordering
            onSubmit={(items) => {
              handleContinue(items);
            }}
            onCancel={() => {}}
          />
        </CartProvider>
      </OrderingWrapper>
    </Wrapper>
  );
};

export default HandyQSR;
